/**
 * archetypeMap — Illustrative model archetypes by risk profile.
 *
 * For each of the five Risk Assessment profiles, defines:
 *   - sleeve: % allocation across the four asset legs:
 *       crypto       — BTC/ETH/SOL etc. positions
 *       equities     — Individual stock positions (passive picks)
 *       stockSignals — Active-management subscription strategy
 *       cash         — Cash + stablecoins for tactical flexibility
 *
 *   - withinStocks: % split between Conservative / Moderate / Aggressive
 *                   stocks (applies to both equities AND stock signals
 *                   sleeves — risk character covers all stock holdings)
 *
 *   - primaryStocksBucket: the user's primary risk-character bucket for
 *                          deep-linking into the Analyst Ratings module
 *
 * ────────────────────────────────────────────────────────────────────
 * PHILOSOPHY (2026-06-17): Equal-thirds across the three growth legs
 * within every profile. Diversification across asset classes is a
 * universal principle — every investor benefits from spreading their
 * deployed capital across Crypto, Equities, and Stock Signals.
 *
 * Cash reserve scales with risk profile: conservatives keep more dry
 * powder (40% for Conservative, down to 2% for Ultra-Aggressive).
 * The three growth legs split the remaining capital EQUALLY.
 *
 * Concrete sleeves (per profile):
 *   Conservative      → 20 / 20 / 20 / 40
 *   Moderate          → 25 / 25 / 25 / 25
 *   Growth-Oriented   → 29 / 28 / 28 / 15
 *   Aggressive        → 33 / 31 / 31 / 5
 *   Ultra-Aggressive  → 34 / 32 / 32 / 2
 *
 * Further differentiation between profiles happens WITHIN each bucket
 * via `withinStocks` (Conservative/Moderate/Aggressive risk-character
 * split of stock holdings), and eventually a within-crypto split when
 * that lands.
 *
 * Compliance benefit: every profile shows the SAME equal-thirds
 * diversification principle. We're not pushing aggressive investors
 * into more crypto or conservatives into more equities — the split
 * across asset classes is identical in shape, just scaled by cash.
 * ────────────────────────────────────────────────────────────────────
 *
 * COMPLIANCE — These are illustrative model structures only. Not
 * personalized advice. Not a list of specific securities to purchase.
 * The card that consumes this data must include the standard
 * "illustrative, not personalized, not a recommendation" disclaimer.
 *
 * The five risk-profile keys MUST match the strings used in the existing
 * Risk Assessment scoring engine (cryptoCalculationUtils.js):
 *   "Conservative", "Moderate", "Growth-Oriented",
 *   "Aggressive", "Ultra-Aggressive"
 *
 * Rename note (2026-06-02): the lowest tier was renamed from
 * "Ultra-Conservative" to "Conservative". `getArchetype` normalizes any
 * legacy "Ultra-Conservative" reference to the new "Conservative" key so
 * historical user data continues to resolve correctly.
 */

const LEGACY_PROFILE_ALIASES = {
  "Ultra-Conservative": "Conservative",
};

// Per-profile cash reserve scales with risk tolerance: conservatives
// keep more dry powder, aggressive deploys more. The three growth legs
// (crypto, equities, stockSignals) split the remainder EQUALLY — that's
// the philosophy that doesn't budge regardless of profile.
//
// Helper: build a sleeve where the non-cash 100% gets split into equal
// thirds across the three growth legs.
const buildSleeve = (cash) => {
  const growth = 100 - cash;
  const third = Math.floor(growth / 3);
  const rounding = growth - third * 3; // 0, 1, or 2 — drop on crypto
  return Object.freeze({
    crypto: third + rounding,
    equities: third,
    stockSignals: third,
    cash,
  });
};

export const ARCHETYPE_MAP = Object.freeze({
  "Conservative": {
    sleeve:        buildSleeve(40),   // 20 / 20 / 20 / 40
    withinStocks:  { Conservative: 60, Moderate: 30, Aggressive: 10 },
    primaryStocksBucket: "Conservative",
  },
  "Moderate": {
    sleeve:        buildSleeve(25),   // 25 / 25 / 25 / 25
    withinStocks:  { Conservative: 50, Moderate: 35, Aggressive: 15 },
    primaryStocksBucket: "Conservative",
  },
  "Growth-Oriented": {
    sleeve:        buildSleeve(15),   // 29 / 28 / 28 / 15
    withinStocks:  { Conservative: 35, Moderate: 40, Aggressive: 25 },
    primaryStocksBucket: "Moderate",
  },
  "Aggressive": {
    sleeve:        buildSleeve(5),    // 33 / 31 / 31 / 5
    withinStocks:  { Conservative: 20, Moderate: 40, Aggressive: 40 },
    primaryStocksBucket: "Moderate",
  },
  "Ultra-Aggressive": {
    sleeve:        buildSleeve(2),    // 34 / 32 / 32 / 2
    withinStocks:  { Conservative: 15, Moderate: 30, Aggressive: 55 },
    primaryStocksBucket: "Aggressive",
  },
});

/**
 * Fallback profile used when the Risk Assessment hasn't fired or returned
 * something unrecognized. Moderate is intentionally chosen as a neutral
 * middle-ground that won't accidentally bias the deep-link too far in
 * either direction.
 */
export const FALLBACK_PROFILE = "Moderate";

export function getArchetype(profile) {
  const normalized = LEGACY_PROFILE_ALIASES[profile] || profile;
  return ARCHETYPE_MAP[normalized] || ARCHETYPE_MAP[FALLBACK_PROFILE];
}
